import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface MediaCoverageItem {
    id: string;
    title: string;
    description: string;
    mediaLogo?: string;
    link: string;
    type: 'article' | 'video';
    createdAt: number;
}

export const ManageMediaCoverage = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [mediaItems, setMediaItems] = useState<MediaCoverageItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<MediaCoverageItem | null>(null);
    const [newItem, setNewItem] = useState({
        title: '',
        description: '',
        link: '',
        type: 'article' as 'article' | 'video',
        imageFile: null as File | null,
        mediaLogo: '',
        contentType: ''
    });

    useEffect(() => {
        fetchMediaCoverage();
    }, []);

    useEffect(() => {
        if (editingItem) {
            setNewItem({
                title: editingItem.title,
                description: editingItem.description,
                link: editingItem.link,
                type: editingItem.type,
                imageFile: null,
                mediaLogo: editingItem.mediaLogo || '',
                contentType: ''
            });
        } else {
            setNewItem({
                title: '',
                description: '',
                link: '',
                type: 'article',
                imageFile: null,
                mediaLogo: '',
                contentType: ''
            });
        }
    }, [editingItem]);

    const fetchMediaCoverage = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/managemediacoverage', {
                action: 'getMediaCoverage',
                data: { eventcode }
            });
            if (response.data.result === 'Success') {
                setMediaItems(response.data.data || []);
            } else {
                toast.error(response.data.msg || 'Failed to fetch media coverage');
            }
        } catch (error) {
            toast.error('Failed to fetch media coverage');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddMedia = async () => {
        if (!newItem.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!newItem.link.trim()) {
            toast.error('Link is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                eventcode,
                title: newItem.title,
                description: newItem.description,
                link: newItem.link,
                type: newItem.type,
                mediaLogo: newItem.mediaLogo
            };
            if (editingItem) {
                data = { ...data, id: editingItem.id } as any;
            }
            if (newItem.imageFile) {
                const imageBase64 = await fileToBase64(newItem.imageFile);
                data = { ...data, imageBase64, imageFileName: newItem.imageFile.name, contentType: newItem.contentType } as any;
            }
            const response = await AxiosAdminInstance.post('/api/admin/managemediacoverage', {
                action: editingItem ? 'edit' : 'add',
                data
            });
            if (response.data.result === 'Success') {
                toast.success(editingItem ? 'Media item updated successfully' : 'Media item added successfully');
                setNewItem({
                    title: '',
                    description: '',
                    link: '',
                    type: 'article',
                    imageFile: null,
                    mediaLogo: '',
                    contentType: ''
                });
                setEditingItem(null);
                fetchMediaCoverage();
            } else {
                toast.error(response.data.msg || 'Operation failed');
            }
        } catch (error) {
            toast.error(editingItem ? 'Failed to update media item' : 'Failed to add media item');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewItem({
            title: '',
            description: '',
            link: '',
            type: 'article',
            imageFile: null,
            mediaLogo: '',
            contentType: ''
        });
        setEditingItem(null);
    };

    const handleDeleteMedia = async (mediaId: string) => {
        if (!window.confirm('Are you sure you want to delete this media item?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/managemediacoverage', {
                action: 'delete',
                data: { eventcode, mediaId }
            });
            if (response.data.result === 'Success') {
                toast.success('Media item deleted successfully');
                fetchMediaCoverage();
            } else {
                toast.error(response.data.msg || 'Failed to delete media item');
            }
        } catch (error) {
            toast.error('Failed to delete media item');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (150KB max)
            if (file.size > 150 * 1024) {
                toast.error('File size must be less than 150KB');
                return false;
            }
            setNewItem(prev => ({ ...prev, imageFile: file, mediaLogo: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const mediaColumns = [
        { name: 'Actions', cell: (row: MediaCoverageItem) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingItem(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteMedia(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'ID', selector: (row: MediaCoverageItem) => row.id, sortable: true },
        { name: 'Type', selector: (row: MediaCoverageItem) => row.type, sortable: true },
        { name: 'Title', selector: (row: MediaCoverageItem) => row.title, sortable: true },
        { name: 'Description', selector: (row: MediaCoverageItem) => row.description, sortable: false },
        { name: 'Link', cell: (row: MediaCoverageItem) => (
            <a href={row.link} target="_blank" rel="noopener noreferrer" title="Open link">
                {row.link.length > 50 ? row.link.substring(0, 50) + '...' : row.link}
            </a>
        ) },
        { name: 'Media Logo', cell: (row: MediaCoverageItem) => (
            row.mediaLogo ? <img src={row.mediaLogo} alt={row.title} style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-newspaper-o fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Media Coverage</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingItem ? 'Edit Media Item' : 'Add New Media Item'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="title"
                                            custplaceholder="Title"
                                            custid="mediaTitle"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, title: val }))}
                                            onExternalBind={newItem.title}
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="link"
                                            custplaceholder="Link URL"
                                            custid="mediaLink"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, link: val }))}
                                            onExternalBind={newItem.link}
                                        />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <DropDownComponent
                                            custname="type"
                                            custplaceholder="Type"
                                            custid="mediaType"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, type: val as 'article' | 'video' }))}
                                            names={['article', 'video']}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="description"
                                            custplaceholder="Description"
                                            custid="mediaDescription"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, description: val }))}
                                            onExternalBind={newItem.description}
                                        />
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="mediaLogo"
                                            custplaceholder="Upload Media Logo"
                                            custid="mediaLogo"
                                            onChange={handleFileChange}
                                        />
                                        {newItem.mediaLogo && (
                                            <div className="mt-2">
                                                <img src={newItem.mediaLogo} alt="Current" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="submit" disabled={loading} onClick={handleAddMedia}>
                                            {editingItem ? 'Update Media Item' : 'Add Media Item'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingItem && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Media Coverage Items</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={mediaColumns}
                                            data={mediaItems}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};